/* eslint-disable react-hooks/rules-of-hooks */
import {
  ElementRef,
  Fragment,
  PropsWithChildren,
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Continuable,
  DigitalTwinSteps,
  DigitalTwinViewProps,
  DraftBound,
  LocalReferenceableUpload,
} from './DigitalTwin.types'
import Text from 'openspace/components/Text'
import { i18n, t, tO } from 'translate/i18n'
import ProgressBar from '@onepercentio/one-ui/components/ProgressBar'
import Container from 'openspace/components/Container'
import { ProgressIndicator } from '@onepercentio/one-ui/components/FileInput/View/Compact'
import {
  useSantitizedPrice,
  useUploadProgress,
} from 'features/CVM88/Form/Form.hook'
import Button from '@onepercentio/one-ui/components/Button'
import Spacing from '@onepercentio/one-ui/components/Spacing'
import { AssetType } from 'core/logic/asset/asset.types'
import { FormFieldInput } from 'features/CVM88/Form/Form.view'
import { FormMode } from 'features/CVM88/Form/Form.types'
import UncontrolledTransition from '@onepercentio/one-ui/components/UncontrolledTransition'
import AttributeView from 'openspace/components/AttributeView'
import AssetDisplayView from 'openspace/components/AssetDisplay/AssetDisplay.view'
import { AssetDisplayMode } from 'openspace/components/AssetDisplay/AssetDisplay.types'
import { extractEnumValues } from 'utility/enum'
import { DigitalTwinTestIds } from './DigitalTwin.e2e'
import { TransitionAnimationTypes } from '@onepercentio/one-ui/components/Transition'
import LoaderDotsIndicator from '@onepercentio/one-ui/components/LoaderDotsIndicator'
import { useReceivableAmount } from 'context/Marketplace'
import { currencyNumberFormatter } from 'core/helpers/formatter'
import { LPTypo } from 'containers/OneUIProvider/OneUIProvider.data'
import CheckBox from '@onepercentio/one-ui/components/CheckBox'
import { ReactComponent as IconPix } from 'assets/icons/icon_pix.svg'
import Input from '@onepercentio/one-ui/components/Input'
import AlertContainer from 'openspace/components/AlertContainer'
import { Address } from 'features/Form/AddressForm/AddressForm.types'
import Styles from './DigitalTwin.module.scss'
import Radio from '@onepercentio/one-ui/components/Radio'
import { useTaxRound } from './DigitalTwin.hooks'
import AsyncWrapper from '@onepercentio/one-ui/components/AsyncWrapper'
import { CHARS_RANGE } from './DigitalTwin.logic'
import { AddressCreation } from 'features/Form/AddressForm/AddressForm.logic'
import useDepChange from '@onepercentio/one-ui/hooks/utility/useDepChange'
import RarumDialog from 'openspace/components/RarumDialog'
import { AddressSchema } from '@onepercentio/rarum-types/_shared'
import useAsyncControl from '@onepercentio/one-ui/hooks/useAsyncControl'
import OfferPriceLogic from 'components/offer/OfferPrice/OfferPrice.logic'
import IconRarum from 'components/IconRarum'
import { AddressContent } from './DigitalTwin.components.view'
export * from "./DigitalTwin.components.view"

const MAXIMUM_CUSTOM_PHOTOS = 4

export function ContinuableAction({
  canContinue,
  onContinue: _onContinue,
  continueLabel = t('generic.continue'),
}: Continuable) {
  const { onContinue, loading, error } = useAsyncControl({
    onContinue: _onContinue,
  })
  return (
    <Button
      variant='filled'
      onClick={onContinue}
      color={error ? 'error' : undefined}
      disabled={canContinue === false || loading}>
      {error ? (
        t('generic.retryBtn')
      ) : (
        <>
          {continueLabel}
          {loading && <LoaderDotsIndicator dotsCount={3} lockSize />}
        </>
      )}
    </Button>
  )
}

/**
 * Allows the user to create a digital twin offer
 **/
export default function DigitalTwinView({
  asset,
  children,
  currentStep,
}: DigitalTwinViewProps) {
  const progress = useMemo(() => {
    const steps = extractEnumValues(DigitalTwinSteps)
    return (steps.indexOf(currentStep) * 100) / steps.length + 1
  }, [currentStep])

  const utRef = useRef<ElementRef<typeof UncontrolledTransition>>(null)
  useDepChange(
    (prevStep) =>
      currentStep < prevStep && utRef.current!.setOrientation('backward'),
    currentStep
  )

  return (
    <Container
      className={Styles.root}
      data-testid={DigitalTwinTestIds.CONTAINER}>
      <div className={Styles.heading}>
        <AssetDisplayView
          mode={AssetDisplayMode.IMAGE}
          src={asset.media.imageUrl!}
          uniqueId={asset.id}
          zoomable={false}
        />
        <div>
          <Text type='h6'>{asset[i18n.language].name}</Text>
          <Spacing size='16' />
          <div className={Styles.attributes}>
            {asset.attributes.map((a) => (
              <AttributeView {...a} key={a.trait_type} />
            ))}
          </div>
        </div>
      </div>
      <Spacing size='16' />
      <ProgressBar progress={progress} size={16} mode='gauge' />
      <Spacing size='40' />
      <UncontrolledTransition ref={utRef} className={Styles.stretch}>
        <Fragment key={currentStep}>{children as ReactElement}</Fragment>
      </UncontrolledTransition>
    </Container>
  )
}

export function ImageUpload({
  asset,
  uploads,
  onUploadImage,
  onRemoveImage,
  retryUpload,
  ...props
}: {
  asset: AssetType
  uploads: LocalReferenceableUpload[]
  onUploadImage?: () => void
  onRemoveImage?: (idx: number) => void
  retryUpload?: () => void
} & Continuable) {
  const mode =
    !onUploadImage || !onRemoveImage ? FormMode.READ_ONLY : FormMode.WRITE
  const uploadStatuses = (() => {
    return new Array(MAXIMUM_CUSTOM_PHOTOS).fill(undefined).map((_, i) => {
      const upload: (typeof uploads)[number] | undefined = uploads[i]
      const snapshotProgressObject = useMemo(() => {
        if (upload) return 'snapshot' in upload.task ? upload.task : true
        else return undefined
      }, [upload?.task])
      const { progress, error } = useUploadProgress(snapshotProgressObject)

      return {
        el: (
          <Fragment key={i}>
            <Button
              variant='transparent-link'
              data-testid={
                progress === 100
                  ? DigitalTwinTestIds.REMOVE_FILE
                  : DigitalTwinTestIds.PREVIEW
              }
              onClick={
                mode === FormMode.WRITE
                  ? upload
                    ? () => onRemoveImage!(i)
                    : onUploadImage
                  : () => {}
              }
              className={Styles.customPhotoBox}>
              <UncontrolledTransition
                contentClassName={Styles.photoUploadContainer}
                transitionType={TransitionAnimationTypes.COIN_FLIP}>
                {progress === 100 ? (
                  <div
                    key={upload.objUrl}
                    className={`${Styles.preview} ${Styles[FormMode[mode]]}`}
                    style={{ backgroundImage: `url(${upload.objUrl})` }}
                  />
                ) : (
                  <ProgressIndicator
                    key={'uploading'}
                    defaultIcon={
                      error ? (
                        <IconRarum
                          data-testid={DigitalTwinTestIds.ERROR_INDICATOR}
                          icon='alert'
                          className={Styles.alert}
                        />
                      ) : typeof progress === 'number' ? (
                        <text
                          key={'progress-num'}
                          x='24'
                          y='24.5'
                          textAnchor='middle'
                          fontSize={10}
                          style={{ fill: 'var(--svg-color, #000)' }}
                          dominantBaseline='middle'>
                          {progress.toFixed(0)}%
                        </text>
                      ) : (
                        <g transform='translate(13.5, 13.5) scale(0.75)'>
                          <path
                            d='M17.5673 14.8269C17.5673 17.3494 15.5225 19.3942 13 19.3942C10.4775 19.3942 8.4327 17.3494 8.4327 14.8269C8.4327 12.3045 10.4775 10.2596 13 10.2596C15.5225 10.2596 17.5673 12.3045 17.5673 14.8269Z'
                            style={{ stroke: 'var(--svg-color, #000)' }}
                          />
                          <path
                            d='M1.125 21.125L1.125 9.99306C1.125 8.1223 2.64155 6.60576 4.5123 6.60576C5.79532 6.60576 6.96822 5.88087 7.542 4.7333L8.30968 3.19795C8.9449 1.9275 10.2434 1.12499 11.6638 1.125L14.3362 1.12501C15.7566 1.12502 17.0551 1.92753 17.6903 3.19796L18.458 4.73333C19.0318 5.8809 20.2047 6.60579 21.4877 6.60579C23.3585 6.60579 24.875 8.12233 24.875 9.99309V21.125C24.875 23.1961 23.1961 24.875 21.125 24.875H4.875C2.80393 24.875 1.125 23.1961 1.125 21.125Z'
                            style={{ stroke: 'var(--svg-color, #000)' }}
                          />
                        </g>
                      )
                    }
                    progress={progress}
                    Wrapper={(p: PropsWithChildren) => <div {...p} />}
                  />
                )}
              </UncontrolledTransition>
            </Button>
            {i === 1 && <br />}
          </Fragment>
        ),
        error,
      }
    })
  })()
  const uploadEls = uploadStatuses.map((e) => e.el)
  const hasError = uploadStatuses.some((e) => !!e.error)
  return (
    <>
      <Text type='h6' code='marketplace.offerCreation.sections.photos.title' />
      <Spacing size='8' />
      <div className={Styles.photosContainer}>
        <AssetDisplayView
          mode={AssetDisplayMode.IMAGE}
          src={asset.media.imageUrl!}
          uniqueId={asset.id}
          zoomable={false}
        />
        <div className={Styles.customPhoto}>
          {uploadEls}
          {mode === FormMode.READ_ONLY ? (
            <span />
          ) : (
            <>
              <br />
              {hasError ? (
                <Button
                  variant='filled'
                  color='error'
                  onClick={() => retryUpload!()}>
                  {t('generic.retryBtn')}
                </Button>
              ) : (
                <Button
                  disabled={uploads.length === MAXIMUM_CUSTOM_PHOTOS}
                  variant='filled'
                  color='secondary'
                  onClick={onUploadImage}>
                  {t('marketplace.offerCreation.sections.photos.actions.add')}
                </Button>
              )}
            </>
          )}
        </div>
      </div>
      <Spacing size='16' />
      {onUploadImage && <ContinuableAction {...props} />}
    </>
  )
}

export function DescriptionView({
  description,
  onChange,
  draft,
  ...props
}: {
  description: string
  onChange?: (description: string) => void
} & Continuable &
  DraftBound) {
  useEffect(() => {
    if (draft?.description) onChange!(draft.description)
  }, [])
  if (!onChange) {
    return (
      <>
        <Text
          type='h6'
          code='marketplace.offerCreation.sections.description.title'
        />
        <Spacing size='16' />
        <Text type={LPTypo.P1}>{description || '-'}</Text>
      </>
    )
  }
  return (
    <>
      <FormFieldInput
        config={{
          type: 'text',
          id: 'description',
          title: (
            <>
              <div className={Styles.descriptionTitle}>
                <Text type={LPTypo.P1Bold}>
                  {t('marketplace.offerCreation.sections.description.title')}
                </Text>
                <Spacing size='stretch' />
                <Text type={LPTypo.P1}>
                  {t('generic.charCount', {
                    count: description.length,
                    total: CHARS_RANGE[1],
                  })}
                </Text>
              </div>
            </>
          ),
          placeholder: t(
            'marketplace.offerCreation.sections.description.title'
          ),
          footer: (val) => <></>,
          multiline: 4,
        }}
        data-testid={DigitalTwinTestIds.DESCRIPTION}
        mode={FormMode.WRITE}
        onAnswer={(_, __, description) => {
          onChange!(description as string)
        }}
        value={description}
      />
      <Spacing size='stretch' />
      <ContinuableAction {...props} />
    </>
  )
}

export function PriceView({
  price,
  setPrice,
  tax,
  draft,
  value,
  ...props
}: {
  price: string
  tax: number | undefined
  setPrice?: (num: string) => void
  value?: [reference: number, min: number, max: number]
} & Continuable &
  DraftBound) {
  useEffect(() => {
    if (draft?.price) setPrice!(draft.price)
  }, [])
  const priceNum = useSantitizedPrice(price)
  const receivableAmount = useReceivableAmount(tax, priceNum)
  const taxView = useTaxRound(tax)
  if (!setPrice) {
    const priceNum = useSantitizedPrice(price)
    return (
      <>
        <Text
          type='h6'
          code='marketplace.offerCreation.sections.currency.title'
        />
        <Spacing size='16' />
        <Text type='h3'>R$ {currencyNumberFormatter(priceNum)}</Text>
      </>
    )
  }

  const priceISDefined = priceNum > 0

  return (
    <>
      <FormFieldInput
        config={{
          type: 'currency',
          id: 'price',
          title: t('marketplace.offerCreation.sections.currency.title'),
        }}
        mode={FormMode.WRITE}
        onAnswer={(_, __, _nextPrice) => {
          const nextPrice = _nextPrice as string
          if (nextPrice.trim() !== '') setPrice!(nextPrice)
        }}
        value={price}
        data-testid={DigitalTwinTestIds.PRICE}
      />
      <div className={Styles.reference}>
        <div>
          <Text
            type={LPTypo.P2}
            code='offer.creation.steps.value.price.reference'
          />
          <Text type={LPTypo.P1Bold}>
            <OfferPriceLogic prices={{ brl: value![0] }} mode='text' />
          </Text>
        </div>
        <div
          className={priceISDefined && value![1] > priceNum ? Styles.fail : ''}>
          <Text type={LPTypo.P2} code='offer.creation.steps.value.price.min' />
          <Text type={LPTypo.P1Bold}>
            <OfferPriceLogic prices={{ brl: value![1] }} mode='text' />
          </Text>
        </div>
        <div
          className={priceISDefined && value![2] < priceNum ? Styles.fail : ''}>
          <Text type={LPTypo.P2} code='offer.creation.steps.value.price.max' />
          <Text type={LPTypo.P1Bold}>
            <OfferPriceLogic prices={{ brl: value![2] }} mode='text' />
          </Text>
        </div>
      </div>
      <Spacing size='16' />
      <div className={Styles.priceDetail}>
        <Text type={LPTypo.P1} code='offer.creation.steps.value.cut' />
        <Text type={LPTypo.P1}>
          {taxView !== undefined ? taxView : <LoaderDotsIndicator />}%
        </Text>
      </div>
      <Spacing size='8' />
      <div className={Styles.priceDetail}>
        <Text type={LPTypo.P1} code='offer.creation.steps.value.receive' />
        <Text type={LPTypo.P1}>
          R${' '}
          {receivableAmount !== undefined ? (
            currencyNumberFormatter(receivableAmount)
          ) : (
            <LoaderDotsIndicator lockSize />
          )}
        </Text>
      </div>
      <Spacing size='stretch' />
      <ContinuableAction {...props} />
    </>
  )
}

export function PaymentMethod<D = string>({
  onChangePaymentData,
  paymentData,
  draft,
  onContinue,
  ...props
}: {
  onChangePaymentData?: (data: D) => void
  paymentData: D
} & Continuable &
  DraftBound) {
  const control = useAsyncControl({
    onContinue,
  })
  useEffect(() => {
    if (draft?.paymentData) onChangePaymentData!(draft.paymentData as D)
  }, [])
  const mode = onChangePaymentData ? FormMode.WRITE : FormMode.READ_ONLY
  return (
    <>
      <Text type='h6' code='authenticated.payment.data.payment' />
      <Spacing size='16' />
      {mode === FormMode.WRITE && (
        <>
          <Text
            type={LPTypo.P2}
            code='offer.creation.steps.payment.disclaimer'
          />
          <Spacing size='16' />
        </>
      )}
      <CheckBox
        className={Styles.method}
        checked
        groupId='methods'
        onToggle={() => {}}
        value=''
        label={
          <>
            <div>
              <div>
                <IconPix /> <Text type={LPTypo.H5}>PIX</Text>{' '}
              </div>
              {mode === FormMode.WRITE ? (
                <Text
                  code='offer.creation.steps.payment.description'
                  type={LPTypo.P2}
                />
              ) : (
                <>
                  <Text type={LPTypo.P2}>{paymentData as string}</Text>
                </>
              )}
            </div>
          </>
        }
      />
      {mode === FormMode.WRITE && (
        <>
          <Spacing size='40' />
          <Text type='h6' code='offer.creation.steps.payment.pix.title' />
          <Spacing size='16' />
          <Text
            type={LPTypo.P2}
            code='offer.creation.steps.payment.pix.description'
          />
          <Spacing size='16' />
          <Input
            error={
              control.error
                ? t('offer.creation.steps.payment.pix.error.invalid')
                : undefined
            }
            placeholder={tO('offer.creation.steps.payment.pix.placeholder')}
            onChange={({ target: { value } }) =>
              onChangePaymentData!(value as D)
            }
            value={paymentData as string}
            data-testid={DigitalTwinTestIds.PIX}
          />
          <Spacing size='16' />
          <AlertContainer type='warning'>
            <Text
              code='offer.creation.steps.payment.pix.disclaimer'
              type={LPTypo.P2}
            />
          </AlertContainer>
          <Spacing size='stretch' />
          <ContinuableAction {...props} onContinue={control.onContinue} />
        </>
      )}
    </>
  )
}

export function OriginAddressSelection({
  onSelectOriginAddress,
  selectedAddress,
  availableAddresses,
  onCancel,
  onReloadAddresses,
  draft,
  mode,
  ...props
}: {
  mode: 'buy' | 'creation'
  onSelectOriginAddress: (address: Address) => void
  selectedAddress?: Address
  availableAddresses: Address[] | null | undefined
} & ClickActions<'onCancel' | 'onReloadAddresses'> &
  Continuable &
  DraftBound) {
  useEffect(() => {
    if (draft?.originAddress && availableAddresses) {
      const address = availableAddresses.find(
        (a) => a.id === draft.originAddress
      )
      if (address) onSelectOriginAddress(address)
    }
  }, [availableAddresses])
  const [addressCreation, setAddressCreation] = useState(false)
  const utRef = useRef<ElementRef<typeof UncontrolledTransition>>(null)
  return (
    <>
      <UncontrolledTransition ref={utRef} className={Styles.stretch}>
        {addressCreation ? (
          <Fragment key={'addressCreation'}>
            <Text
              code='offer.creation.steps.address.actions.addAddress'
              type={LPTypo.H5}
            />
            <Spacing size='32' />
            <AddressCreation
              onCancel={() => {
                utRef.current!.setOrientation('backward')
                setAddressCreation(false)
              }}
              onCreated={() => {
                setAddressCreation(false)
                onReloadAddresses()
              }}
            />
          </Fragment>
        ) : (
          <Fragment key={'addressSelection'}>
            <Text code={`offer.${mode}.steps.address.title`} type={LPTypo.H5} />
            {mode === 'creation' && (
              <Text
                code='offer.creation.steps.address.description'
                type={LPTypo.P2}
              />
            )}
            <Spacing size='8' />
            <AsyncWrapper
              error={availableAddresses === null}
              loading={availableAddresses === undefined}
              onClose={onCancel}
              onRetry={onReloadAddresses}>
              {availableAddresses?.map((add, _idx) => {
                const idx = String(_idx)
                return (
                  <div key={add.id}>
                    <Radio
                      data-testid={DigitalTwinTestIds.ADDRESS_OPTION}
                      className={Styles.address}
                      groupId='address'
                      label={<AddressContent address={add} />}
                      value={idx}
                      onToggle={() => {
                        onSelectOriginAddress(add)
                      }}
                      checked={selectedAddress === add}
                    />
                  </div>
                )
              })}
            </AsyncWrapper>
            <Button
              variant={'outline'}
              color='primary'
              onClick={() => setAddressCreation(true)}>
              {tO('offer.creation.steps.address.actions.addAddress')}
            </Button>
            <Spacing size='stretch' />
            <ContinuableAction {...props} />
          </Fragment>
        )}
      </UncontrolledTransition>
    </>
  )
}

export function Resume({
  asset,
  uploadedImages,
  description,
  price,
  paymentData,
  onTerms,
  ...props
}: {
  asset: AssetType
  uploadedImages: LocalReferenceableUpload[]
  description: string
  price: [price: string, tax: number]
  paymentData: string
} & ClickActions<'onTerms'> &
  Continuable) {
  const taxView = useTaxRound(price[1])
  return (
    <>
      <ImageUpload asset={asset} uploads={uploadedImages} {...props} />
      <Spacing size='40' />
      <DescriptionView description={description} {...props} />
      <Spacing size='40' />
      <PriceView price={price[0]} tax={taxView} {...props} />
      <Spacing size='40' />
      <PaymentMethod paymentData={paymentData} {...props} />
      <Spacing size='16' />
      <AlertContainer type='warning' noTitle>
        <Text type={LPTypo.P1}>
          {tO('offer.creation.steps.confirmation.title', {
            termsAction: (
              <Button variant='transparent-link' onClick={onTerms} key='action'>
                <b color='secondary'>{t('generic.termsOfService')}</b>
              </Button>
            ),
          })}
        </Text>
      </AlertContainer>
      <Spacing size='32' />
      <Spacing size='stretch' />
      <ContinuableAction {...props} />
    </>
  )
}

export function ConfirmationView({
  onCancel,
  onSaveWithDraft,
  onSaveWithoutDraft,
  open,
}: Partial<
  ClickActions<'onSaveWithDraft' | 'onSaveWithoutDraft' | 'onCancel'>
> & { open: boolean }) {
  return (
    <RarumDialog
      onClose={onCancel!}
      open={open}
      title={[
        'time',
        t('marketplace.offerCreation.sections.confirmation.title'),
      ]}
      cancel={[
        t('marketplace.offerCreation.sections.confirmation.actions.exit'),
        onSaveWithoutDraft!,
      ]}
      confirm={[
        t('marketplace.offerCreation.sections.confirmation.actions.save'),
        onSaveWithDraft!,
      ]}>
      <Text
        type={LPTypo.P1}
        code='marketplace.offerCreation.sections.confirmation.description'
      />
    </RarumDialog>
  )
}
